import { humanize } from '@utils';
import { BACKEND_NAME_SEPARATOR, MAX_DIMENSION, NAME_SEPARATOR } from './alertConfig.constants';

export const isFirstDimension = (dimension) => dimension === 1;

export const getOptionsList = (options) => options.map((item) => ({ value: item }));

export const getDimensionState = (dimension) => [...Array(dimension)];

export const updateDimensionState = (state, ind, select) => {
  const newState = [...state];
  newState[ind] = select;

  return newState;
};

export const getFilteredOptions = (input, option) =>
  (option?.value.toLowerCase() ?? '').includes(input.toLowerCase());

export const getDimensionNameList = (name: string): string[] =>
  name.split(NAME_SEPARATOR).map((name) => name.trim());

export const getDimensionNameArr = (selectList) =>
  selectList.map((dimension) => dimension?.value ?? '');

export const getDimensionNameStr = (selectList) =>
  getDimensionNameArr(selectList).join(NAME_SEPARATOR);

export const getBackendNameList = (name: string): string[] =>
  name.split(BACKEND_NAME_SEPARATOR).map((name) => name.trim());

export const getBackendNameArr = (selectList) =>
  selectList.map((dimension) => dimension?.backendName ?? '');

export const getBackendNameStr = (selectList) =>
  getBackendNameArr(selectList).join(BACKEND_NAME_SEPARATOR);

export const getInsertIndex = (aiAlertsConfig, level) =>
  aiAlertsConfig[level].dimensionData.findIndex(({ isCustomDimension }) => !isCustomDimension);

export const getDimensionList = (aiConfigList, aiDimensionMapping) =>
  aiConfigList
    .filter(({ level }) => level === 1)
    .reduce((dimAcc, curr) => {
      const { dimensionData } = curr;
      const dimensionException = ['ASSETS', 'OBJECT']; // Exception to hide dimensions at level 2 & level 3 combinations dropdown list
      dimAcc = dimensionData
        .filter(({ isBeingDeleted, backendName }) => {
          if (isBeingDeleted) return false;
          if (
            dimensionException.includes(backendName) &&
            !aiDimensionMapping[backendName].isCustomDimension
          )
            return false;
          return true;
        })
        .map((dimension) => ({ value: dimension.name, ...dimension }));

      return dimAcc;
    }, []);

export const getNewDimensionCombination = (selectList, level) => {
  return {
    name: getDimensionNameStr(selectList),
    backendName: getBackendNameStr(selectList),
    isCustomDimension: true,
    level,
    cardinality: 0,
  };
};

export const transformDimensionList = (aiAlertMapping = {}, aiConfigList) => {
  return Object.keys(aiAlertMapping)
    .filter((backendName) => {
      const dimensionExceptions = ['CHANNEL', 'STREAMING_PROTOCOL']; // Exception to hide dimensions in the dropdown list
      // const groupByGeoDimensions = ['GEO_COUNTRY', 'GEO_CONTINENT', 'GEO_ASN', 'GEO_DMA'];    // Geo dimensions list
      const geoGroupByExceptions = ['GEO_STATE']; // Exception to hide group_by items in the dropdown list, GEO_STATE is always hidden

      if (!dimensionExceptions.includes(backendName)) {
        const { isCustomDimension, field } = aiAlertMapping[backendName];

        if (!geoGroupByExceptions.includes(field)) {
          return isCustomDimension;
        }
      }
    })
    .filter((backendName) => {
      // filter out items in the dropdown select, if the dimension is already present in level 1.
      return !aiConfigList?.[1]?.dimensionData.some((item) => item.backendName === backendName);
    })
    .map((backendName) => {
      const currDimension = aiAlertMapping[backendName];
      const { pulse5Label } = currDimension;
      const dimensionMeta = {
        value: pulse5Label,
        backendName,
      };

      return dimensionMeta;
    });
};

export const getExtensionText = (hilightDimensions) => {
  let text = '';
  const levels = hilightDimensions.map(({ level }) => level);
  const setLevels = [...new Set(levels)];
  setLevels.forEach((level) => {
    if (level === 1) text = 'dimension';
    else if (setLevels.length < 2) text = 'dimension combinations';
    else text = 'dimension combinations below';
  });

  return text;
};

export const customRootCauseCount = (dimensionData, maxCount) => {
  let count = 0;
  dimensionData.forEach(({ isCustomDimension, isBeingDeleted }) => {
    if (!isBeingDeleted && isCustomDimension) count += 1;
  });

  return count < maxCount;
};

export const isAdvanceCustomerType = (customerType) => {
  return customerType === 2;
};

export const shouldDisplayAddDim = (isFeatureFlagEnabled, dimensionData = [], maxCustomCount) => {
  if (!isFeatureFlagEnabled) return false;

  return customRootCauseCount(dimensionData, maxCustomCount);
};

export const getDeletionCombination = (state, combBackendName): boolean => {
  return state.some(({ backendName }) => backendName === combBackendName);
};

export const getCardinalityText = (
  cardinality,
  level,
  isFetching = false,
  isError = false,
  isBeingAdded = false,
  isBeingDeleted = false,
) => {
  switch (true) {
    case isBeingAdded:
      return 'Addition processing';
    case isBeingDeleted:
      return 'Deletion processing';
    case isFetching:
      return 'Loading..';
    case isError:
      return 'Error';
    default: {
      const prefixText = level === 0 ? 'Scanning ' : ''; // prefix for 0th level
      const suffixText = level === 1 ? 'Value' : 'Combination'; // Checking whether it's single dimension
      const isPlural = cardinality > 1 ? 's' : '';

      return `${prefixText}${humanize(cardinality, '')} ${suffixText}${isPlural}`;
    }
  }
};

export const getDeleteText = (isBeingAdded: boolean, isBeingDeleted: boolean): string => {
  switch (true) {
    case isBeingAdded:
      return 'Traffic may be too low if waiting more than 24 hours. Try another dimension.';
    case isBeingDeleted:
      return 'Deletion from the system takes upto 1 hour time';
    default:
      return '';
  }
};

export const getHilightCombinations = (aiConfigList, combination, level, backendName) => {
  const subCombinations = [
    {
      backendName,
      level,
    },
  ];

  aiConfigList.forEach((levelData) => {
    const { dimensionData, level: currLevel } = levelData;
    if (currLevel > level && currLevel < MAX_DIMENSION) {
      dimensionData.forEach((combinationData) => {
        const { name, isCustomDimension, backendName, isBeingDeleted } = combinationData;
        const nameList = getDimensionNameList(name);
        const hasCombination =
          isCustomDimension &&
          !isBeingDeleted &&
          combination.every((dimension) => nameList.some((name) => name === dimension));
        if (hasCombination) {
          const combinationDetails = {
            backendName,
            level: currLevel,
          };
          subCombinations.push(combinationDetails);
        }
      });
    }
  });

  return subCombinations;
};

export const getHilightColor = (shallDelete: boolean, isBeingDeleted: boolean): string => {
  switch (true) {
    case shallDelete:
      return '#FF7F7F';
    case isBeingDeleted:
      return '#979797';
    default:
      return '';
  }
};
